import { useEffect, useState } from 'react'
import { apiGetJson } from '../lib/apiClient'
import { unwrapFirst } from '../lib/apiUnwrap'
import { X } from 'lucide-react'

type BannerSettings = {
  enabled: boolean
  text: string
  speed: number
  position: 'top' | 'bottom'
  height: number
  opacity: number
  closable: boolean
  autoHide: boolean
  autoHideDelay: number
}

const DEFAULT_SETTINGS: BannerSettings = {
  enabled: true,
  text: '',
  speed: 12,
  position: 'top',
  height: 40,
  opacity: 1,
  closable: true,
  autoHide: false,
  autoHideDelay: 10,
}

export default function ScrollingBanner() {
  const [config, setConfig] = useState<BannerSettings | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    let mounted = true

    const parseBoolean = (input: unknown, fallback: boolean) => {
      if (typeof input === 'boolean') return input
      if (typeof input === 'number') return input !== 0
      if (typeof input === 'string') {
        const normalized = input.trim().toLowerCase()
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true
        if (['false', '0', 'no', 'off'].includes(normalized)) return false
      }
      return fallback
    }

    const normalizeRawValue = (value: unknown): Record<string, unknown> | null => {
      if (value && typeof value === 'object') return value as Record<string, unknown>
      if (typeof value !== 'string') return null

      const trimmed = value.trim()
      if (!trimmed) return null

      try {
        const parsed = JSON.parse(trimmed)
        if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>
        if (typeof parsed === 'string' && parsed.trim()) {
          return { ...DEFAULT_SETTINGS, text: parsed.trim() }
        }
      } catch {
        // Backward compatibility: plain text setting means "enabled banner with default config".
        return { ...DEFAULT_SETTINGS, text: trimmed }
      }

      return null
    }

    const normalize = (value: any) => {
      const normalizedValue = normalizeRawValue(value)
      if (!normalizedValue) return null

      const speed = Number(normalizedValue.speed)
      const height = Number(normalizedValue.height)
      const opacity = Number(normalizedValue.opacity)
      const autoHideDelay = Number(normalizedValue.autoHideDelay)

      return {
        enabled: parseBoolean(normalizedValue.enabled, DEFAULT_SETTINGS.enabled),
        text: String(normalizedValue.text || '').trim(),
        speed: Number.isFinite(speed) && speed > 0 ? speed : DEFAULT_SETTINGS.speed,
        position: normalizedValue.position === 'bottom' ? 'bottom' : 'top',
        height: Number.isFinite(height) && height > 0 ? height : DEFAULT_SETTINGS.height,
        opacity: Number.isFinite(opacity) && opacity >= 0 ? opacity : DEFAULT_SETTINGS.opacity,
        closable: parseBoolean(normalizedValue.closable, DEFAULT_SETTINGS.closable),
        autoHide: parseBoolean(normalizedValue.autoHide, DEFAULT_SETTINGS.autoHide),
        autoHideDelay: Number.isFinite(autoHideDelay) && autoHideDelay >= 0
          ? autoHideDelay
          : DEFAULT_SETTINGS.autoHideDelay,
      } as BannerSettings
    }

    const fetchConfig = async () => {
      try {
        const resp = await apiGetJson<any>('/api/banner-settings')
        const raw = unwrapFirst<unknown>(resp)
        const next = normalize(raw)
        if (!mounted) return
        setConfig(next)
        setLoaded(true)
        if (next) setIsVisible(true)
      } catch {
        if (mounted) {
          setConfig(null)
          setLoaded(true)
        }
      }
    }

    void fetchConfig()
    const timer = setInterval(fetchConfig, 60_000)

    return () => {
      mounted = false
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (config?.autoHide && isVisible && config.autoHideDelay > 0) {
        const timer = setTimeout(() => {
            setIsVisible(false)
        }, config.autoHideDelay * 1000)
        return () => clearTimeout(timer)
    }
  }, [config, isVisible])

  if (!loaded) return null
  if (!config || !config.enabled || !config.text) return null
  if (!isVisible) return null

  const isTop = config.position !== 'bottom'
  
  // Style for fixed positioning if bottom, or just normal flow/sticky handling if top?
  // The user requirement says "Tùy chọn vị trí hiển thị (trên cùng/dưới cùng)"
  // Usually bottom banner is fixed. Top banner can be fixed or static.
  // Since it's replacing a static banner in Header, let's make it sticky or static at top, 
  // OR fixed if user wants it always visible.
  // Given "banner thông báo", fixed is common.
  
  const positionClasses = isTop
    ? 'sticky z-40 shadow-sm'
    : 'fixed bottom-0 left-0 right-0 z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'
  const topOffsetStyle = isTop ? { top: 'var(--customer-header-height, 64px)' } : null

  return (
    <div
        className={`w-full bg-yellow-300 text-black transition-all duration-500 ease-in-out overflow-hidden ${positionClasses}`}
        style={{
            ...(topOffsetStyle || {}),
            height: `${config.height}px`,
            opacity: config.opacity,
            display: isVisible ? 'flex' : 'none'
        }}
    >
      <div className="w-full relative h-full flex items-center">
        <div className="flex-1 overflow-hidden h-full flex items-center">
            <div 
                className="whitespace-nowrap animate-marquee"
                style={{ 
                    animationDuration: `${config.speed}s`,
                    paddingLeft: '100%'
                }}
            >
                {config.text}
            </div>
        </div>
        
        {config.closable && (
            <button 
                onClick={() => setIsVisible(false)}
                className="absolute right-4 z-10 p-1 bg-yellow-300/80 hover:bg-yellow-400 rounded-full transition-colors shadow-sm"
                title="Đóng thông báo"
            >
                <X className="h-5 w-5" />
            </button>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          display: inline-block;
          animation: marquee linear infinite;
        }
      `}} />
    </div>
  )
}

